import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Button, TextField, Typography, Alert, FormControlLabel, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { clientsAPI } from '../../services/api';
import { NAVIGATION } from '../../constants';

const schema = z.object({
  name: z.string().min(2),
  email: z.string().email(),
  phone: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().optional(),
  company: z.string().optional(),
  isDomestic: z.boolean().optional(),
  pib: z.string().optional(),
  registrationNumber: z.string().optional(),
  startDate: z.string().optional()
});

type FormData = z.infer<typeof schema>;

const AddClient: React.FC = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, watch, control } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      zipCode: '',
      country: '',
      company: '',
      isDomestic: false,
      pib: '',
      registrationNumber: '',
      startDate: ''
    }
  });
  const [success, setSuccess] = React.useState('');
  const [error, setError] = React.useState('');

  const onSubmit = async (data: FormData) => {
    setSuccess(''); setError('');
    try {
      const payload = {
        ...data,
        startDate: data.startDate ? new Date(data.startDate).toISOString() : undefined,
      };
      await clientsAPI.create(payload);
      setSuccess('Client added successfully!');
      reset();
      navigate(NAVIGATION.CLIENTS);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add client');
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 0 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Add New Client</Typography>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Name" fullWidth margin="normal" {...register('name')} error={!!errors.name} helperText={errors.name?.message} />
            <TextField label="Company" fullWidth margin="normal" {...register('company')} error={!!errors.company} helperText={errors.company?.message} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Email" fullWidth margin="normal" {...register('email')} error={!!errors.email} helperText={errors.email?.message} />
            <TextField label="Phone" fullWidth margin="normal" {...register('phone')} error={!!errors.phone} helperText={errors.phone?.message} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Address" fullWidth margin="normal" {...register('address')} error={!!errors.address} helperText={errors.address?.message} />
            <TextField label="Zip Code" fullWidth margin="normal" {...register('zipCode')} error={!!errors.zipCode} helperText={errors.zipCode?.message} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="City" fullWidth margin="normal" {...register('city')} error={!!errors.city} helperText={errors.city?.message} />
            <TextField label="Country" fullWidth margin="normal" {...register('country')} error={!!errors.country} helperText={errors.country?.message} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField label="Start Date" type="date" fullWidth margin="normal" {...register('startDate')} InputLabelProps={{ shrink: true }} error={!!errors.startDate} helperText={errors.startDate?.message} />
          </Box>
          
          {/* Domestic Client Section */}
          <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Controller
                  name="isDomestic"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value || false}
                    />
                  )}
                />
              }
              label="Domestic Client (Serbian company)"
            />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 2 }}>
              <TextField 
                label="PIB (Tax ID)" 
                fullWidth 
                margin="normal" 
                {...register('pib')} 
                error={!!errors.pib} 
                helperText={errors.pib?.message}
                disabled={!watch('isDomestic')}
              />
              <TextField 
                label="Registration Number" 
                fullWidth 
                margin="normal" 
                {...register('registrationNumber')} 
                error={!!errors.registrationNumber} 
                helperText={errors.registrationNumber?.message}
                disabled={!watch('isDomestic')}
              />
            </Box>
          </Box>
        </Box>

        <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 3 }} disabled={isSubmitting}>
          {isSubmitting ? 'Adding...' : 'Add Client'}
        </Button>
      </form>
    </Box>
  );
};

export default AddClient; 